// @flow
import * as React from 'react'

import styled from 'styled-components'
import get from 'lodash/get';
import type { ReactComponentStyled } from 'styled-components'
import type { RoomDataType } from '@/models/property'

import {
  getLowestRoomRate,
  getRateLowestPrice,
  getRoomImages,
  isRoomRefundable,
  RefundableStatuses,
  roomAllowsPayLater,
} from '@/helpers'
import {
  Button,
  Paper,
  PriceComponent,
  Tooltip,
} from '@/components'
import Strings, { Images } from '@/constants'

import styles from './styles.scss'

const RefundableStatus: ReactComponentStyled<{refundable: boolean}> = styled.span`
  color: ${
    // eslint-disable-next-line no-nested-ternary
    props => props.refundable === RefundableStatuses.REFUNDABLE
      ? styles.green1
      : props.refundable === RefundableStatuses.PARTIALLY_REFUNDABLE
        ? styles.blue1
        : styles.orange1
  } !important;
  font-size: 15px;
  font-weight: 700;
`

type RoomCardProps = {
  room: RoomDataType,
  chooseRoom: (room: RoomDataType) => void
}

const RoomCard = ({ room, chooseRoom }: RoomCardProps) => {
  const rate = getLowestRoomRate(room)
  const pricing = rate ? getRateLowestPrice(rate) : undefined
  const exclusivePricing = pricing ? parseFloat(get(pricing, 'totals.exclusive.billable_currency.value')) : null
  const inclusivePricing = pricing ? parseFloat(get(pricing, 'totals.inclusive.billable_currency.value')) : null
  const refundable = isRoomRefundable(room)
  const payLater = roomAllowsPayLater(room)
  const images = getRoomImages(room.images)

  const roomImage = React.useMemo(() => images.length > 0
    ? images[0]
    : { href: Images.hotelWithPalms, caption: room.name }
  , [images])

  return (
    <Paper className={styles.roomCard}>
      <div className={styles.roomImage}>
        <img src={roomImage.href} alt={roomImage.caption} />
      </div>
      <div className={styles.roomInformation}>
        <h3>{room.name}</h3>
        <div className={styles.description}
          dangerouslySetInnerHTML={{ __html: room.descriptions.overview }}
        />
        {payLater && (
          <Tooltip
            content={<p>{Strings.infoCard.payLaterTooltip}</p>}
            minWidth="250px"
          >
          <div className={styles.payLaterTag}>
            {Strings.infoCard.payLater}
          </div>
          </Tooltip>
        )}
        <div className={styles.isRefundable}>
          <RefundableStatus refundable={refundable}>
            {
              // eslint-disable-next-line no-nested-ternary
              refundable === RefundableStatuses.REFUNDABLE
                ? Strings.infoCard.refundable
                : refundable === RefundableStatuses.PARTIALLY_REFUNDABLE
                  ? Strings.infoCard.someRefundable
                  : Strings.infoCard.nonRefundable
            }
          </RefundableStatus>
        </div>
      </div>
      <div className={styles.pricing}>
        {
          pricing && inclusivePricing && exclusivePricing
            ? (
              <>
                <div className={styles.priceSection}>
                  <PriceComponent rate={rate} hideRefundableLabel />
                </div>
                <Button
                  className={styles.chooseRoom}
                  title={Strings.infoCard.chooseRoom}
                  primary
                  ariaLabel="ChooseRoom"
                  onClick={() => chooseRoom(room)}
                />
                <div className={styles.mobileOnly}>
                  <Button
                    title={Strings.infoCard.chooseRoom}
                    primary
                    ariaLabel="ChooseRoom"
                    onClick={() => chooseRoom(room)}
                    className={styles.chooseRoomButton}
                  />
                </div>
              </>
            )
            : (
              <p>
                {Strings.infoCard.noPrice}
              </p>
            )
        }
      </div>
    </Paper>
  )
}

export default RoomCard
